<script setup>
// This allows to add details of activities from PM (asset-scope) to organizational level BI data warehouse.
import { storeToRefs } from 'pinia';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

const emit = defineEmits(['close']);

const $t = inject('$t');
const $toast = inject('$toast');

const project_management_store = useProjectManagementStore();
const { active_schedule, active_task, is_fullscreen, dw_org_sync } = storeToRefs(project_management_store);
const { fetch_dw_org_sync, create_dw_org_sync } = project_management_store;

const state = reactive({
  form_data: {},
  is_saving: false,
  is_deleting: false,
});

const is_save_disabled = computed(() => {
  return !!dw_org_sync.value.find(org_sync => org_sync.name === state.form_data.name && org_sync.uid !== active_task.value.uid);
});

async function onRemove() {
  try {
    state.is_deleting = true;
    await create_dw_org_sync({
      uid: active_task.value.uid,
      schedule: active_schedule.value.uid,
      enable: false,
    });
    emit('close');
    state.is_deleting = false;
    $toast({
      title: $t('Success'),
      type: 'success',
    });
  }
  catch {
    $toast({
      title: $t('Something went wrong'),
      text: $t('Please try again'),
      type: 'error',
    });
    state.is_deleting = false;
  }
}

async function onSave() {
  try {
    state.is_saving = true;
    await create_dw_org_sync({
      uid: active_task.value.uid,
      schedule: active_schedule.value.uid,
      name: state.form_data.name,
      enable: true,
    });
    emit('close');
    state.is_saving = false;
    $toast({
      title: $t('Success'),
      text: $t('Activity has been synced successfully'),
      type: 'success',
    });
  }
  catch {
    $toast({
      title: $t('Something went wrong'),
      text: $t('Please try again'),
      type: 'error',
    });
    state.is_saving = false;
  }
}

onMounted(async () => {
  if (active_task.value?.data_warehouse_config?.organization_sync?.enabled)
    state.form_data.name = active_task.value.data_warehouse_config.organization_sync.name;
  if (!dw_org_sync.value.length)
    await fetch_dw_org_sync();
});
</script>

<template>
  <HawkModalContainer
    :options="{
      teleportTo: is_fullscreen
        ? '#pm-fullscreen-container'
        : 'body',
    }"
  >
    <Vueform
      v-model="state.form_data"
      size="sm"
      sync
      :display-errors="false"
      :display-messages="false"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
      :endpoint="onSave"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            <div class="text-lg font-semibold text-gray-900">
              {{ $t('Sync to organization') }}
            </div>
          </template>
        </HawkModalHeader>
        <HawkModalContent>
          <div class="text-sm font-normal text-gray-600 mb-6">
            {{ $t('pm-sync-to-organization-text-1') }}
            <br>
            <br>
            <span class="font-semibold text-gray-800">
              {{ $t('Note') }}:
            </span>
            {{ $t('pm-sync-to-organization-text-2') }}
          </div>
          <TextElement
            name="name"
            :label="$t('Name')"
            :rules="['required']"
          />
        </HawkModalContent>
        <HawkModalFooter>
          <template #right>
            <div class="flex w-full" :class="active_task?.data_warehouse_config?.organization_sync?.enabled ? 'justify-between' : 'justify-end'">
              <div v-if="active_task?.data_warehouse_config?.organization_sync?.enabled">
                <HawkButton
                  :loading="state.is_deleting"
                  type="plain"
                  color="error"
                  @click="onRemove"
                >
                  {{ $t('Remove') }}
                </HawkButton>
              </div>

              <div class="flex">
                <HawkButton
                  class="mr-4"
                  type="outlined"
                  @click="emit('close')"
                >
                  {{ $t('Cancel') }}
                </HawkButton>
                <ButtonElement
                  name="save"
                  :disabled="is_save_disabled"
                  :loading="state.is_saving"
                  :submits="true"
                >
                  {{ $t('Sync') }}
                </ButtonElement>
              </div>
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
